import { memo, useCallback } from "react";
import Chip, { type ChipProps } from "@mui/material/Chip";
import ResourceLayout from 'ui/layouts/Resource';
import Resource from "ui/widgets/Resource";
import {
    type GridRowParams,
    type GridRowIdGetter,
    type GridRenderCellParams,
    GRID_ACTIONS_COLUMN_TYPE,
} from "@mui/x-data-grid-premium";
import { TableGridMode } from "consts/table";
import { FilterVariant } from "consts/filters";
import { multi, Status, text } from "consts/users";
import type { User as UserType } from "features/users/types";
import {
    selectIsUsersLoading,
    selectIsUsersUninitialized,
    selectUserSlice
} from "features/users/selectors";
import useUserRoles from "features/users/useUserRoles";
import { DeleteUser, ToggleActive } from "./UserActions";
import UserToolbar from "./UserToolbar";
import UserCreateEdit from "./UserCreateEdit";
import useUsers from "./useUsers";

const getStatusChipColor = (status?: string): ChipProps['color'] => {
    switch (status) {
        case Status.Pending:
            return 'warning';
        case Status.Inactive:
            return 'error';
        case Status.Active:
            return 'success';
        default:
            return 'default';
    }
};

const User = () => {
    useUserRoles();

    const getRowId: GridRowIdGetter<UserType> = useCallback(({ coreId }: UserType) =>
        coreId, []);

    return (
        <ResourceLayout
            breadcrumbsProps={{
                heading: 'Users',
                links: [{ name: '' }]
            }}
        >
            <Resource
                name='User'
                useResource={useUsers}
                mode={TableGridMode.User}
                filterTypes={new Map<FilterVariant, string[]>([
                    [FilterVariant.MultiOption, multi],
                    [FilterVariant.Text, text]
                ])}
                selectIsTableUninitialized={selectIsUsersUninitialized}
                selectIsTableLoading={selectIsUsersLoading}
                selectTableDataSlice={selectUserSlice}
                getRowId={getRowId}
                Toolbar={UserToolbar}
                Detail={UserCreateEdit}
                pinnedColumns={{
                    right: [GRID_ACTIONS_COLUMN_TYPE]
                }}
                columns={[
                    {
                        field: 'coreId',
                        headerName: 'User ID',
                        minWidth: 130,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.coreId`}>
                                {params.value}
                            </div>
                        )
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                        minWidth: 200,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.email`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'firstName',
                        headerName: 'User First Name',
                        minWidth: 200,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.firstName`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'lastName',
                        headerName: 'User Last Name',
                        minWidth: 200,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.lastName`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'roleDisplayName',
                        headerName: 'Role',
                        minWidth: 200,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.roleDisplayName`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        minWidth: 160,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.status`}>
                                <Chip
                                    variant='outlined'
                                    label={params.value}
                                    color={getStatusChipColor(params.value)}
                                />
                            </div>
                        )
                    },
                    {
                        field: 'lastLogin',
                        headerName: 'Last login',
                        minWidth: 160,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.lastLogin`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'createdAt',
                        headerName: 'Created',
                        minWidth: 160,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.createdAt`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: 'updatedAt',
                        headerName: 'Updated',
                        minWidth: 160,
                        renderCell: (params: GridRenderCellParams) => (
                            <div data-test-id={`users.row.${params.row.coreId}.cell.updatedAt`}>
                                {params.value || '-'}
                            </div>
                        )
                    },
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        width: 100,
                        maxWidth: 100,
                        getActions: (params: GridRowParams) => [
                            <ToggleActive
                                {...params}
                            />,
                            <DeleteUser
                                {...params}
                            />
                        ]
                    }
                ]}
                LayoutProps={{
                    sx: {
                        mx: 0
                    }
                }}
                FiltersProps={{
                    sx: {
                        mx: 0
                    }
                }}
            />
        </ResourceLayout>
    );
};

export default memo(User);
